import { useState } from 'react';
import { cn } from '@/lib/utils.js';
import { imageSrc } from '../../lib/imageSrc.js';

// Flat navy+orange two-tone (matches the prototype kiosk/board avatars):
// solid navy or orange disc with white initials, picked by name hash.
const PALETTES = [
  { bg: '#1B2A4A', text: '#FFFFFF' }, // navy-600
  // לבן על הכתום של המותג נותן 2.76:1 — ראשי תיבות ב-9px שאי-אפשר לקרוא.
  // הכתום לא זז; הטקסט עובר לנייבי — 5.12:1, וזה בדיוק הצמד של הלוגו עצמו.
  { bg: '#F47920', text: '#1B2A4A' }, // orange-500 + navy-600
];

function getAvatarColors(name: string): { bg: string; text: string } {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return PALETTES[hash % PALETTES.length]!;
}

function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0]![0] ?? '') + (words[1]![0] ?? '');
  }
  return (words[0] ?? '').slice(0, 2);
}

type Size = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLS: Record<Size, string> = {
  sm: 'w-6 h-6 text-[9px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
  xl: 'w-32 h-32 text-2xl',
};

type Props = {
  name: string;
  photoUrl?: string | null;
  size?: Size;
  className?: string;
  title?: string;
};

export function WorkerAvatar({ name, photoUrl, size = 'md', className, title }: Props): JSX.Element {
  const [imgError, setImgError] = useState(false);
  const { bg, text } = getAvatarColors(name);
  const init = initials(name);

  if (photoUrl && !imgError) {
    const src = imageSrc(photoUrl) ?? '';
    return (
      <img
        src={src}
        alt={name}
        title={title ?? name}
        onError={() => setImgError(true)}
        className={cn('rounded-full object-cover flex-shrink-0 select-none', SIZE_CLS[size], className)}
      />
    );
  }

  return (
    <span
      className={cn('inline-flex items-center justify-center rounded-full font-bold flex-shrink-0 select-none', SIZE_CLS[size], className)}
      style={{ backgroundColor: bg, color: text }}
      title={title ?? name}
      aria-label={name}
    >
      {init}
    </span>
  );
}
